import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Form,
  Input,
  Tag,
  Select,
  List,
  Row,
  Col,
  Spin,
  Empty,
  Collapse,
  Divider,
  message,
  Tooltip,
  Alert,
  Upload,
  Progress,
  Image,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  DownOutlined,
  CheckCircleOutlined,
  PictureOutlined,
  ThunderboltOutlined,
  AuditOutlined,
  UploadOutlined,
  SyncOutlined,
  VideoCameraOutlined,
  SkinOutlined,
  EditOutlined,
  ExpandOutlined,
} from '@ant-design/icons';
import CharacterImageModal from './CharacterImageModal';
import PanelImageModal from './PanelImageModal';
import BulkPanelModal from './BulkPanelModal';
import { AdminTaskTracker } from '../../components/admin/AdminTaskTracker';
import { 
  SourceProcess, 
  SourceChapter, 
  SourceCharacter, 
  ExtractedCharacter, 
  SourceScene, 
  ExtractedScene, 
  SourcePanel, 
  ExtractedPanel,
  AdminTaskType,
  AdminTaskStatus,
  AdminTask,
  SceneCharacterReference,
} from '../../types';
import { sourceStoriesApi, CreateSourceProcessRequest, SavePanelsRequest, adminTasksApi } from '../../services/api';
import { useApi, useAsyncOperation, useThemeColors } from '../../hooks';
import { formatDate } from '../../utils';
import { playAdminTaskNotificationSound, sendBrowserNotification } from '../../utils/videoNotifications';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const countWords = (text?: string) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).length;
};

interface ProcessTabProps {
  storyId: string;
  chapters: SourceChapter[];
}

const ProcessTab: React.FC<ProcessTabProps> = ({ storyId, chapters }) => {
  const themeColors = useThemeColors();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<SourceProcess | null>(null);
  const [isExtractModalVisible, setIsExtractModalVisible] = useState(false);
  const [extractedCharacters, setExtractedCharacters] = useState<ExtractedCharacter[]>([]);
  const [form] = Form.useForm();

  // Scene-related state
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [activeSceneKeys, setActiveSceneKeys] = useState<Record<string, string | string[]>>({});
  const [activeDetailKeys, setActiveDetailKeys] = useState<Record<string, string | string[]>>({});
  const [scenesData, setScenesData] = useState<Record<string, SourceScene[]>>({});
  const [scenesLoading, setScenesLoading] = useState<Record<string, boolean>>({});
  const [extractedScenes, setExtractedScenes] = useState<ExtractedScene[]>([]);
  const [isScenePreviewModalVisible, setIsScenePreviewModalVisible] = useState(false);
  const [currentExtractingProcessId, setCurrentExtractingProcessId] = useState<string | null>(null);

  // Panel-related state
  const [panelsData, setPanelsData] = useState<Record<string, SourcePanel[]>>({});
  const [panelsLoading, setPanelsLoading] = useState<Record<string, boolean>>({});
  const [extractedPanels, setExtractedPanels] = useState<ExtractedPanel[]>([]);
  const [extractedAppearanceChanges, setExtractedAppearanceChanges] = useState<any[]>([]);
  const [isPanelPreviewModalVisible, setIsPanelPreviewModalVisible] = useState(false);
  const [currentExtractingSceneId, setCurrentExtractingSceneId] = useState<string | null>(null);
  const [selectedSceneForPanels, setSelectedSceneForPanels] = useState<SourceScene | null>(null);
  const [mediaStrategy, setMediaStrategy] = useState<'ALL_IMAGE' | 'ALL_VIDEO' | 'AUTO_MIX'>('AUTO_MIX');
  const [isMediaStrategyModalVisible, setIsMediaStrategyModalVisible] = useState(false);
  const [pendingExtractScene, setPendingExtractScene] = useState<SourceScene | null>(null);
  
  const [isPanelImageModalVisible, setIsPanelImageModalVisible] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState<SourcePanel | null>(null);

  // Bulk Panel Image Generation state
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
  const [selectedSceneForBulk, setSelectedSceneForBulk] = useState<SourceScene | null>(null);

  // Re-extract Panels state
  const [isReExtractModalVisible, setIsReExtractModalVisible] = useState(false);
  const [reExtractScene, setReExtractScene] = useState<SourceScene | null>(null);
  const [reExtractComment, setReExtractComment] = useState('');
  const [reExtractMediaStrategy, setReExtractMediaStrategy] = useState<'ALL_IMAGE' | 'ALL_VIDEO' | 'AUTO_MIX'>('AUTO_MIX');
  const [reExtractStep, setReExtractStep] = useState<'input' | 'preview'>('input');
  const [reExtractedPanels, setReExtractedPanels] = useState<ExtractedPanel[]>([]);

  // AI Review Scene state
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [reviewScene, setReviewScene] = useState<SourceScene | null>(null);
  const [reviewResult, setReviewResult] = useState<{ critiques: string[]; issues: Array<{ tag: string; description: string }>; suggestedComment: string } | null>(null);

  // Character Image Management state
  const [isCharacterImageModalVisible, setIsCharacterImageModalVisible] = useState(false);
  const [selectedCharacterForImage, setSelectedCharacterForImage] = useState<SourceCharacter | null>(null);

  // Epic 4: Scene Outfit Reference state
  const [outfitRefsData, setOutfitRefsData] = useState<Record<string, any[]>>({});
  const [outfitRefsLoading, setOutfitRefsLoading] = useState<Record<string, boolean>>({});
  const [planningOutfitSceneId, setPlanningOutfitSceneId] = useState<string | null>(null);
  const [generatingRefImageId, setGeneratingRefImageId] = useState<string | null>(null);
  const [uploadingOutfitRefId, setUploadingOutfitRefId] = useState<string | null>(null);
  const [editingOutfitRef, setEditingOutfitRef] = useState<SceneCharacterReference | null>(null);
  const [isEditOutfitModalVisible, setIsEditOutfitModalVisible] = useState(false);
  const [outfitEditForm] = Form.useForm();

  const [autoGenerateTaskId, setAutoGenerateTaskId] = useState<string | null>(null);
  const [currentAutoTask, setCurrentAutoTask] = useState<AdminTask | null>(null);
  const [pendingTaskCount, setPendingTaskCount] = useState<number>(0);
  const [batchGenLoading, setBatchGenLoading] = useState(false);
  const refreshedProcessIdsRef = useRef<Set<string>>(new Set());
  const completedSubtasksRef = useRef<Set<string>>(new Set());
  const lastPanelRefreshRef = useRef<number>(0);

  // Fetch existing active auto-generate task on mount
  useEffect(() => {
    const fetchActiveTask = async () => {
      try {
        const [r1, r2] = await Promise.all([
          adminTasksApi.getTasks({
            type: AdminTaskType.AUTO_GENERATE_PROCESS_CONTENT,
            status: 'PENDING,IN_PROGRESS,FAILED',
            pageSize: 100
          }),
          adminTasksApi.getTasks({
            type: AdminTaskType.BATCH_GEN_MEDIA,
            status: 'PENDING,IN_PROGRESS,FAILED',
            pageSize: 10
          })
        ]);
        const response = { data: [...r1.data, ...r2.data] };
        
        console.log('[ProcessTab] Active tasks found:', response.data.length);
        
        // Find the task that belongs to this storyId in its payload
        const activeTask = response.data.find(t => {
          const detail = t.detail as any;
          return detail.targetId === storyId || detail.payload?.sourceStoryId === storyId;
        });

        if (activeTask) {
          console.log('[ProcessTab] Found active task for story:', activeTask.id);
          setAutoGenerateTaskId(activeTask.id);
          setCurrentAutoTask(activeTask);
        } else {
          console.log('[ProcessTab] No active task for storyId:', storyId);
        }
      } catch (error) {
        console.error('Failed to fetch active tasks:', error);
      }
    };
    fetchActiveTask();
  }, [storyId]);

  const { data: allPanelsData, refresh: refreshAllPanels } = useApi(
    useCallback(() => sourceStoriesApi.getAllSourcePanels(storyId), [storyId]),
    { immediate: true }
  );
  const allPanels = React.useMemo(() => allPanelsData?.data || [], [allPanelsData]);

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch processes
  const {
    data: processesData,
    loading: processesLoading,
    refresh: refreshProcesses,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceProcesses(storyId), [storyId]),
    { immediate: true }
  );

  // Fetch characters for the story to display names instead of IDs
  const {
    data: storyCharactersData,
    refresh: refreshCharacters,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceCharacters(storyId, { pageSize: 1000 }), [storyId]),
    { immediate: true }
  );

  const storyCharacters = React.useMemo(() => storyCharactersData?.data || [], [storyCharactersData]);
  const characterMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    storyCharacters.forEach(c => {
      map[c.id] = c.name;
    });
    return map;
  }, [storyCharacters]);

  const getCharacterName = (id: string) => characterMap[id] || `Unknown (${id.substring(0, 8)})`;

  // Epic 4: Outfit reference handlers
  const loadOutfitRefs = async (sceneId: string) => {
    setOutfitRefsLoading(prev => ({ ...prev, [sceneId]: true }));
    try {
      const result = await sourceStoriesApi.getSceneCharacterReferences(storyId, sceneId);
      setOutfitRefsData(prev => ({ ...prev, [sceneId]: result.data || [] }));
    } catch (error) {
      console.error('Failed to load outfit refs:', error);
    } finally {
      setOutfitRefsLoading(prev => ({ ...prev, [sceneId]: false }));
    }
  };

  // Reload outfit refs cho TẤT CẢ scene đang mở (đã có data). Dùng sau thao tác có thể
  // ảnh hưởng nhiều scene — vd sửa outfit lan sang các scene sau theo continuity.
  const reloadLoadedOutfitRefs = async () => {
    const loadedSceneIds = Object.keys(outfitRefsData);
    await Promise.all(loadedSceneIds.map(id => loadOutfitRefs(id)));
  };

  const handlePlanOutfits = async (scene: SourceScene) => {
    setPlanningOutfitSceneId(scene.id);
    try {
      await sourceStoriesApi.planSceneOutfits(storyId, scene.id);
      message.success('Outfit planning completed!');
      // Replan có thể lan outfit sang các scene sau -> reload tất cả scene đang mở
      await reloadLoadedOutfitRefs();
    } catch (error: any) {
      message.error(error.message || 'Outfit planning failed');
    } finally {
      setPlanningOutfitSceneId(null);
    }
  };

  const handleGenRefImage = async (refId: string, sceneId: string, forceRegenerate: boolean = false) => {
    setGeneratingRefImageId(refId);
    try {
      await sourceStoriesApi.generateSceneReferenceImage(storyId, refId, forceRegenerate);
      message.success(forceRegenerate ? 'Reference image regenerated (forced)!' : 'Reference image generated!');
      // Ảnh outfit có thể được đẩy xuống các scene sau (Unchanged) -> reload tất cả scene đang mở
      await reloadLoadedOutfitRefs();
    } catch (error: any) {
      message.error(error.message || 'Failed to generate reference image');
    } finally {
      setGeneratingRefImageId(null);
    }
  };

  // Helper: Get outfit status for badge
  const getOutfitStatus = (sceneId: string) => {
    const refs = outfitRefsData[sceneId] || [];
    if (refs.length === 0) return { text: 'Not planned', color: 'default' };
    return { text: `${refs.length} reference(s)`, color: 'success' };
  };

  const handleDeleteOutfitRef = async (refId: string, sceneId: string) => {
    try {
      await sourceStoriesApi.deleteSceneCharacterReference(storyId, refId);
      message.success('Reference deleted!');
      loadOutfitRefs(sceneId);
    } catch (error: any) {
      message.error(error.message || 'Failed to delete reference');
    }
  };

  const handleEditOutfitRef = (ref: SceneCharacterReference) => {
    setEditingOutfitRef(ref);
    outfitEditForm.setFieldsValue({
      outfitDescription: ref.outfitDescription || ''
    });
    setIsEditOutfitModalVisible(true);
  };

  const handleSaveOutfitDescription = async () => {
    if (!editingOutfitRef) return;
    
    try {
      const values = await outfitEditForm.validateFields();
      await executeAsync(
        () => sourceStoriesApi.updateSceneCharacterReference(storyId, editingOutfitRef.id, {
          outfitDescription: values.outfitDescription
        }),
        { successMessage: 'Outfit description updated!' }
      );
      
      // Sửa outfit có thể lan sang các scene sau (continuity) -> reload tất cả scene đang mở
      await reloadLoadedOutfitRefs();

      setIsEditOutfitModalVisible(false);
      setEditingOutfitRef(null);
      outfitEditForm.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Failed to update outfit description');
    }
  };

  const handleRegenerateOutfitImage = async () => {
    if (!editingOutfitRef) return;
    
    // Save description first, then regenerate
    try {
      const values = await outfitEditForm.validateFields();
      await sourceStoriesApi.updateSceneCharacterReference(storyId, editingOutfitRef.id, {
        outfitDescription: values.outfitDescription
      });
      
      setIsEditOutfitModalVisible(false);
      
      // Regenerate image
      setGeneratingRefImageId(editingOutfitRef.id);
      message.loading({ content: 'Regenerating outfit image...', key: 'regen-outfit' });
      
      await sourceStoriesApi.generateSceneReferenceImage(storyId, editingOutfitRef.id);
      
      message.success({ content: 'Outfit image regenerated!', key: 'regen-outfit' });

      // Sửa outfit có thể lan sang các scene sau (continuity) -> reload tất cả scene đang mở
      await reloadLoadedOutfitRefs();

      setEditingOutfitRef(null);
      outfitEditForm.resetFields();
    } catch (error: any) {
      message.error({ content: error.message || 'Failed to regenerate outfit image', key: 'regen-outfit' });
    } finally {
      setGeneratingRefImageId(null);
    }
  };

  const handleUploadOutfitImage = async (refId: string, sceneId: string, file: File) => {
    setUploadingOutfitRefId(refId);
    message.loading({ content: 'Uploading outfit image...', key: 'upload-outfit' });
    try {
      await sourceStoriesApi.uploadOutfitImage(storyId, refId, file);
      message.success({ content: 'Outfit image uploaded!', key: 'upload-outfit' });
      await loadOutfitRefs(sceneId);
    } catch (error: any) {
      message.error({ content: error.message || 'Failed to upload outfit image', key: 'upload-outfit' });
    } finally {
      setUploadingOutfitRefId(null);
    }
  };

  const getProcessCompletionStats = useCallback((processId: string) => {
    const processPanels = allPanels.filter((p: SourcePanel) => p.processId === processId);
    if (processPanels.length === 0) return { isComplete: false, completedCount: 0, totalCount: 0 };

    const totalCount = processPanels.length;
    const completedCount = processPanels.filter((p: SourcePanel) => !!p.imagePath).length;
    
    return {
      isComplete: completedCount === totalCount && totalCount > 0,
      completedCount,
      totalCount
    };
  }, [allPanels]);

  const getSceneDoneCount = useCallback((sceneId: string) => {
    const scenePanels = allPanels.filter((p: SourcePanel) => p.sceneId === sceneId);
    if (scenePanels.length === 0) return 0;

    return scenePanels.filter((p: SourcePanel) => !!p.imagePath).length;
  }, [allPanels]);

  const handleCreate = () => {
    form.resetFields();

    // Determine the next chapter to start from
    let nextChapterId = undefined;
    if (chapters && chapters.length > 0) {
      if (!processesData || processesData.data.length === 0) {
        // If no processes yet, start from the first chapter
        nextChapterId = chapters[0].id;
      } else {
        // Find the maximum index used in any existing process
        let maxIndex = -1;
        processesData.data.forEach(p => {
          p.chapterIds.forEach(id => {
            const idx = chapters.findIndex(c => c.id === id);
            if (idx > maxIndex) maxIndex = idx;
          });
        });

        // The next chapter is the one right after the highest index found
        if (maxIndex !== -1 && maxIndex < chapters.length - 1) {
          nextChapterId = chapters[maxIndex + 1].id;
        } else if (maxIndex === -1) {
          // Fallback to first chapter if somehow no chapters matched
          nextChapterId = chapters[0].id;
        }
      }
    }

    if (nextChapterId) {
      form.setFieldsValue({ startChapterId: nextChapterId });
    }

    setIsModalVisible(true);
  };

  const handleDelete = async (process: SourceProcess) => {
    await executeAsync(
      () => sourceStoriesApi.deleteSourceProcess(storyId, process.id),
      { successMessage: 'Process deleted successfully!' }
    );
    refreshProcesses();
  };

  const handleViewDetail = (process: SourceProcess) => {
    setSelectedProcess(process);
    setIsDetailModalVisible(true);
  };

  const handleExtractCharacters = async (process: SourceProcess) => {
    setSelectedProcess(process);
    const result = await executeAsync(
      () => sourceStoriesApi.extractCharacters(storyId, process.id),
      { successMessage: 'AI character extraction completed!' }
    );
    if (result && result.data) {
      setExtractedCharacters(result.data);
      setIsExtractModalVisible(true);
    }
  };

  const handleImportCharacters = async () => {
    if (!selectedProcess) return;
    
    // Auto select ALL extracted characters (both new and old) as per user request
    const charactersToImport = extractedCharacters;

    if (charactersToImport.length === 0) {
      message.warning('No characters found to import.');
      return;
    }

    await executeAsync(
      () => sourceStoriesApi.saveExtractedCharacters(storyId, selectedProcess.id, { characters: charactersToImport }),
      { successMessage: `Successfully updated characters for this process!` }
    );

    setIsExtractModalVisible(false);
    // Refresh characters first to ensure we have the new records
    await refreshCharacters();
    // Then refresh processes to get the updated characterIds
    await refreshProcesses();
  };

  // Scene handlers
  const fetchScenes = async (processId: string) => {
    setScenesLoading(prev => ({ ...prev, [processId]: true }));
    try {
      const result = await sourceStoriesApi.getProcessScenes(storyId, processId);
      const scenes = result.data || [];
      setScenesData(prev => ({ ...prev, [processId]: scenes }));
      
      // Eagerly load outfit refs for all scenes to show correct badge immediately
      scenes.forEach((scene: SourceScene) => {
        if (scene.totalPanels && scene.totalPanels > 0 && !outfitRefsData[scene.id]) {
          loadOutfitRefs(scene.id);
        }
      });
    } catch (error) {
      message.error('Failed to load scenes');
    } finally {
      setScenesLoading(prev => ({ ...prev, [processId]: false }));
    }
  };

  const handleExtractScenes = async (process: SourceProcess) => {
    setCurrentExtractingProcessId(process.id);
    const result = await executeAsync(
      () => sourceStoriesApi.extractScenes(storyId, process.id),
      { successMessage: 'AI scene extraction completed!' }
    );
    if (result && result.data) {
      setExtractedScenes(result.data);
      setSelectedProcess(process);
      setIsScenePreviewModalVisible(true);
    }
    setCurrentExtractingProcessId(null);
  };

  const handleSaveScenes = async () => {
    if (!selectedProcess) return;

    await executeAsync(
      () => sourceStoriesApi.saveProcessScenes(storyId, selectedProcess.id, { scenes: extractedScenes }),
      { successMessage: 'Scenes saved successfully!' }
    );

    setIsScenePreviewModalVisible(false);
    // Refresh scenes for this process
    await fetchScenes(selectedProcess.id);
  };

  const handleApproveProcess = async (process: SourceProcess) => {
    await executeAsync(
      () => sourceStoriesApi.approveProcess(storyId, process.id),
      { successMessage: 'Process approved successfully!' }
    );
    refreshProcesses();
    await fetchScenes(process.id);
  };

  // Panel handlers
  const fetchPanels = async (sceneId: string) => {
    setPanelsLoading(prev => ({ ...prev, [sceneId]: true }));
    try {
      const result = await sourceStoriesApi.getSourcePanels(storyId, sceneId);
      const panels = result.data || [];
      setPanelsData(prev => ({ ...prev, [sceneId]: panels }));
    } catch (error) {
      message.error('Failed to load panels');
    } finally {
      setPanelsLoading(prev => ({ ...prev, [sceneId]: false }));
    }
  };
  
  const handleExtractPanels = (scene: SourceScene) => {
    setPendingExtractScene(scene);
    setMediaStrategy('AUTO_MIX');
    setIsMediaStrategyModalVisible(true);
  };

  const handleConfirmExtractPanels = async () => {
    if (!pendingExtractScene) return;
    setIsMediaStrategyModalVisible(false);
    setCurrentExtractingSceneId(pendingExtractScene.id);
    setSelectedSceneForPanels(pendingExtractScene);
    const result = await executeAsync(
      () => sourceStoriesApi.extractPanels(storyId, pendingExtractScene.id, mediaStrategy, true),  // useNewFlow: true
      { successMessage: 'AI panel extraction completed!' }
    );
    if (result && result.data) {
      // Handle new response format: { panels, appearanceChanges }
      const data = result.data as any;
      if (data.panels) {
        setExtractedPanels(data.panels);
        setExtractedAppearanceChanges(data.appearanceChanges || []);
      } else {
        // Backward compatibility: old format returns panels directly
        setExtractedPanels(result.data as any);
        setExtractedAppearanceChanges([]);
      }
      setIsPanelPreviewModalVisible(true);
    }
    setCurrentExtractingSceneId(null);
    setPendingExtractScene(null);
  };

  const handleSavePanels = async () => {
    if (!selectedSceneForPanels) return;

    const payload: SavePanelsRequest = {
      processId: selectedSceneForPanels.processId,
      panels: extractedPanels,
      appearanceChanges: extractedAppearanceChanges,
    };

    const result = await executeAsync(
      () => sourceStoriesApi.saveExtractedPanels(storyId, selectedSceneForPanels.id, payload),
      { 
        successMessage: extractedAppearanceChanges.length > 0 
          ? `Panels saved! ${extractedAppearanceChanges.length} character checkpoint(s) created.`
          : 'Panels saved successfully!' 
      }
    );

    setIsPanelPreviewModalVisible(false);
    
    // Show checkpoint notification if any were created
    const resultData = result?.data as any;
    if (resultData?.checkpointsCreated && resultData.checkpointsCreated > 0) {
      const details: any[] = resultData.checkpointDetails || [];
      const characterNames = details.map((d: any) => d.characterName).filter((name: string, idx: number, arr: string[]) => arr.indexOf(name) === idx).join(', ');
      Modal.info({
        title: `Đã tạo checkpoint cho: ${characterNames}`,
        content: (
          <div>
            <p>Phát hiện thay đổi ngoại hình và đã lưu thành checkpoint:</p>
            <List
              size="small"
              dataSource={details}
              renderItem={(checkpoint: any) => (
                <List.Item>
                  <strong>{checkpoint.characterName}</strong> (Panel {checkpoint.panelOrder}): {checkpoint.changeDescription}
                </List.Item>
              )}
              style={{ marginTop: 12, marginBottom: 12 }}
            />
            <p>Đã tự động tạo ảnh avatar cho các checkpoint trên.</p>
          </div>
        ),
        width: 600,
        okText: 'Đến trang Characters',
        onOk: () => {
          // Switch to Characters tab
          // Note: You may need to add navigation logic here
        }
      });
    }
    
    // Refresh panels for this scene
    await fetchPanels(selectedSceneForPanels.id);
    // Refresh scenes to update the panel count in the header
    await fetchScenes(selectedSceneForPanels.processId);
    // Refresh processes to update content completion status
    refreshProcesses();

    // 🆕 AUTO: Plan outfits + Generate outfit images after saving panels
    await autoProcessOutfits(selectedSceneForPanels);
  };

  // 🆕 Auto process outfits: Plan + Generate images
  const autoProcessOutfits = async (scene: SourceScene) => {
    setPlanningOutfitSceneId(scene.id);
    try {
      // Step 1: Plan outfits
      message.loading({ content: 'Planning outfits...', key: 'outfit-process' });
      await sourceStoriesApi.planSceneOutfits(storyId, scene.id);
      
      // Step 2: Load outfit refs
      const outfitRefs = await sourceStoriesApi.getSceneCharacterReferences(storyId, scene.id);
      const refs = outfitRefs.data || [];
      
      if (refs.length === 0) {
        message.warning({ content: 'No outfit references to generate', key: 'outfit-process' });
        return;
      }

      // Step 3: Generate images for all refs in parallel
      message.loading({ content: `Generating ${refs.length} outfit reference images...`, key: 'outfit-process' });
      
      const generatePromises = refs.map((ref: SceneCharacterReference) => 
        sourceStoriesApi.generateSceneReferenceImage(storyId, ref.id)
          .catch((error) => {
            console.error(`Failed to generate image for ref ${ref.id}:`, error);
            return null; // Continue even if one fails
          })
      );
      
      await Promise.all(generatePromises);
      
      // Step 4: Reload outfit refs to show images
      await loadOutfitRefs(scene.id);
      
      message.success({ 
        content: `✅ Outfits planned and ${refs.length} reference images generated!`, 
        key: 'outfit-process',
        duration: 3
      });
    } catch (error: any) {
      message.error({
        content: error.message || 'Failed to process outfits automatically',
        key: 'outfit-process'
      });
      console.error('Auto outfit processing error:', error);
    } finally {
      setPlanningOutfitSceneId(null);
    }
  };

  const handleRowExpand = (expanded: boolean, record: SourceProcess) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.id]);
      // Load scenes when expanding
      if (!scenesData[record.id]) {
        fetchScenes(record.id);
      }
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id));
    }
  };

  const handleSceneExpand = (sceneId: string) => {
    if (!panelsData[sceneId]) {
      fetchPanels(sceneId);
    }
    // Also load outfit refs if not loaded yet
    if (!outfitRefsData[sceneId]) {
      loadOutfitRefs(sceneId);
    }
  };

  const handleManagePanelImage = (panel: SourcePanel) => {
    setSelectedPanel(panel);
    setIsPanelImageModalVisible(true);
  };

  const handleDeletePanel = (panel: SourcePanel) => {
    Modal.confirm({
      title: 'Xác nhận xóa Panel',
      content: `Bạn có chắc chắn muốn xóa Panel #${panel.sceneOrderIndex}? Các panel phía sau sẽ tự động được đánh lại số thứ tự.`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await sourceStoriesApi.deleteSourcePanel(storyId, panel.sceneId, panel.id);
          message.success('Đã xóa panel thành công');
          fetchPanels(panel.sceneId);
          fetchScenes(panel.processId);
          refreshProcesses();
        } catch (error) {
          message.error('Không thể xóa panel');
        }
      },
    });
  };

  const handleAiRefinePanel = (panel: SourcePanel) => {
    const values = {
      comment: '',
      strategy: 'AUTO' as 'AUTO' | 'IMAGE' | 'VIDEO'
    };

    Modal.confirm({
      title: 'Hiệu chỉnh nội dung bằng AI',
      icon: <ThunderboltOutlined />,
      width: 600,
      content: (
        <div style={{ marginTop: 16 }}>
          <Text strong>Chiến lược Media:</Text>
          <Select
            defaultValue="AUTO"
            onChange={(val: any) => { values.strategy = val; }}
            style={{ width: '100%', marginTop: 8, marginBottom: 16 }}
            options={[
              { value: 'AUTO', label: '🤖 Auto (AI tự quyết định)' },
              { value: 'IMAGE', label: '🖼️ Image (Bắt buộc là ảnh tĩnh)' },
              { value: 'VIDEO', label: '🎬 Video (Bắt buộc là video)' },
            ]}
          />
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Yêu cầu hiệu chỉnh:
          </Text>
          <Input.TextArea
            rows={4}
            placeholder="Ví dụ: Cho nhân vật A khóc nức nở, viết lại lời thoại cho xúc động hơn..."
            onChange={(e) => { values.comment = e.target.value; }}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '12px' }}>
            AI sẽ viết lại Caption, Visual Description và cập nhật danh sách nhân vật dựa trên yêu cầu của bạn.
          </Text>
        </div>
      ),
      okText: 'Gửi yêu cầu',
      cancelText: 'Hủy',
      onOk: async () => {
        if (!values.comment || !values.comment.trim()) {
          message.warning('Vui lòng nhập yêu cầu hiệu chỉnh');
          return Promise.reject();
        }
        try {
          await sourceStoriesApi.aiRefinePanel(storyId, panel.sceneId, panel.id, values.comment, values.strategy);
          message.success('Đã hiệu chỉnh nội dung thành công!');
          fetchPanels(panel.sceneId);
          refreshProcesses();
        } catch (error) {
          message.error('Lỗi khi hiệu chỉnh nội dung bằng AI');
        }
      },
    });
  };

  const handleManageCharacterImage = (character: SourceCharacter) => {
    setSelectedCharacterForImage(character);
    setIsCharacterImageModalVisible(true);
  };

  const handleAutoGenerate = async () => {
    try {
      const response = await adminTasksApi.createTask({
        type: AdminTaskType.AUTO_GENERATE_PROCESS_CONTENT,
        detail: {
          payload: { sourceStoryId: storyId, generateMode: 'setup_only' },
          targetId: storyId,
          progress: 0,
          subtasks: [
            { id: 'create_process', name: 'Tạo Process từ chapter tiếp theo', status: AdminTaskStatus.PENDING },
            { id: 'extract_characters', name: 'Trích xuất nhân vật', status: AdminTaskStatus.PENDING },
            { id: 'suggest_character_names', name: 'Gợi ý tên nhân vật', status: AdminTaskStatus.PENDING },
            { id: 'generate_character_avatars', name: 'Tạo avatar nhân vật mới', status: AdminTaskStatus.PENDING },
            { id: 'extract_scenes', name: 'Trích xuất Scene', status: AdminTaskStatus.PENDING },
            { id: 'extract_panels', name: 'Trích xuất Panel', status: AdminTaskStatus.PENDING },
            { id: 'create_checkpoints', name: 'Tạo Character Checkpoints', status: AdminTaskStatus.PENDING },
            { id: 'scene_outfit_planning', name: 'Lên kế hoạch trang phục', status: AdminTaskStatus.PENDING },
          ]
        }
      });
      setAutoGenerateTaskId(response.data.id);
      message.success('Đã bắt đầu tiến trình tự động tạo nội dung!');
    } catch (error) {
      message.error('Không thể bắt đầu tiến trình tự động');
    }
  };

  const handleAutoCreateMedia = async (process: SourceProcess) => {
    Modal.confirm({
      title: 'Tự động tạo ảnh + video toàn bộ panels',
      content: `Sẽ localize, tạo ảnh minh họa và video cho tất cả panels của "${process.name}". Quá trình có thể mất vài tiếng. Tiếp tục?`,
      okText: 'Tiếp tục',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const response = await adminTasksApi.createTask({
            type: AdminTaskType.AUTO_GENERATE_PROCESS_CONTENT,
            detail: {
              payload: { sourceStoryId: storyId, generateMode: 'media_only', processId: process.id },
              targetId: storyId,
              progress: 0,
              subtasks: [
                { id: 'localize_panels', name: 'Localize Panel', status: AdminTaskStatus.PENDING },
                { id: 'generate_all_panel_media', name: 'Gen ảnh + video toàn bộ panels', status: AdminTaskStatus.PENDING },
              ]
            }
          });
          setAutoGenerateTaskId(response.data.id);
          message.success('Đã bắt đầu tiến trình tạo media!');
        } catch (error) {
          message.error('Không thể bắt đầu tiến trình tạo media');
        }
      }
    });
  };

  const refreshPendingTaskCount = useCallback(async () => {
    try {
      const response = await adminTasksApi.getTasks({
        type: AdminTaskType.BATCH_GEN_MEDIA,
        status: 'IN_PROGRESS',
        pageSize: 10
      });
      const batchTask = response.data.find((t: AdminTask) => {
        const detail = t.detail as any;
        return detail.targetId === storyId || detail.payload?.sourceStoryId === storyId;
      });
      if (batchTask) {
        const pending = ((batchTask.detail as any)?.subtasks || []).filter((s: any) => s.status === 'PENDING').length;
        setPendingTaskCount(pending);
      } else {
        setPendingTaskCount(0);
      }
    } catch {
      // ignore
    }
  }, [storyId]);

  const handleBatchGenMedia = () => {
    Modal.confirm({
      title: 'Batch Gen ảnh + video',
      content: 'Sẽ tự động tạo ảnh + video cho tất cả processes đã có caption. Các processes đang có task chạy sẽ bị bỏ qua. Tiếp tục?',
      okText: 'Tiếp tục',
      cancelText: 'Hủy',
      onOk: async () => {
        setBatchGenLoading(true);
        try {
          const response = await sourceStoriesApi.batchGenMedia(storyId);
          const { tasksCreated, taskId } = response.data;
          if (taskId) setAutoGenerateTaskId(taskId);
          message.success(`Đã bắt đầu gen media cho ${tasksCreated} process...`);
          await refreshPendingTaskCount();
        } catch (error: any) {
          const msg = error?.response?.data?.message || 'Không thể bắt đầu batch gen media';
          message.error(msg);
        } finally {
          setBatchGenLoading(false);
        }
      }
    });
  };

  useEffect(() => {
    refreshPendingTaskCount();
  }, [refreshPendingTaskCount]);

  const handleTaskComplete = useCallback((task: AdminTask) => {
    setCurrentAutoTask(task);
    // Clear flag — không còn suppress video notifications nữa
    localStorage.removeItem('auto_process_media_running');
    refreshProcesses();
    refreshAllPanels();
    refreshPendingTaskCount();
    playAdminTaskNotificationSound('success');
    sendBrowserNotification('✅ Auto Process hoàn thành!', 'Toàn bộ nội dung đã được tạo xong.');
    message.success('Tiến trình tự động hoàn thành!');
  }, [refreshProcesses, refreshAllPanels, refreshPendingTaskCount]);

  const handleTaskFail = useCallback((task: AdminTask) => {
    setCurrentAutoTask(task);
    // Clear flag khi task fail
    localStorage.removeItem('auto_process_media_running');
    refreshPendingTaskCount();
    const errorMsg = (task.detail as any)?.error || 'Tiến trình tự động thất bại';
    playAdminTaskNotificationSound('error');
    sendBrowserNotification('❌ Auto Process thất bại!', errorMsg);
    message.error(`Tiến trình tự động thất bại: ${errorMsg}`);
  }, [refreshPendingTaskCount]);

  const handleTaskRestart = useCallback(async () => {
    if (!autoGenerateTaskId) return;
    await adminTasksApi.restartTask(autoGenerateTaskId);
    completedSubtasksRef.current = new Set();
    lastPanelRefreshRef.current = 0;
    localStorage.removeItem('auto_process_media_running');
    message.info('Đang khởi động lại tiến trình...');
  }, [autoGenerateTaskId]);

  const handleTaskDelete = useCallback(async () => {
    if (!autoGenerateTaskId) return;
    await adminTasksApi.deleteTask(autoGenerateTaskId);
    setAutoGenerateTaskId(null);
    setCurrentAutoTask(null);
    message.success('Đã xóa task.');
  }, [autoGenerateTaskId]);

  const handleTaskUpdate = useCallback((task: AdminTask) => {
    setCurrentAutoTask(task);

    const subtasks: any[] = task.detail.subtasks || [];
    const processId = subtasks.find((s: any) => s.id === 'create_process')?.result?.processId;

    for (const subtask of subtasks) {
      const key = `${subtask.id}:${subtask.status}`;
      if (completedSubtasksRef.current.has(key)) continue;

      if (subtask.status === AdminTaskStatus.COMPLETED) {
        completedSubtasksRef.current.add(key);

        switch (subtask.id) {
          case 'create_process':
            if (processId && !refreshedProcessIdsRef.current.has(processId)) {
              refreshedProcessIdsRef.current.add(processId);
              refreshProcesses();
            }
            break;
          case 'suggest_character_names':
          case 'generate_character_avatars':
            refreshCharacters();
            break;
          case 'extract_scenes':
            refreshProcesses();
            if (processId) fetchScenes(processId);
            break;
          case 'extract_panels':
          case 'localize_panels':
          case 'generate_first_panel_image':
            refreshAllPanels();
            if (processId && scenesData[processId]) {
              scenesData[processId].forEach((scene: any) => fetchPanels(scene.id));
            }
            break;
          case 'generate_all_panel_media':
            refreshAllPanels();
            if (processId && scenesData[processId]) {
              scenesData[processId].forEach((scene: any) => fetchPanels(scene.id));
            }
            break;
        }
      }
    }

    // Periodic panel refresh while generate_all_panel_media is in progress
    const mediaSubtask = subtasks.find((s: any) => s.id === 'generate_all_panel_media');
    if (mediaSubtask?.status === AdminTaskStatus.IN_PROGRESS) {
      // Set flag processId để suppress per-video notifications chỉ cho process này
      // Video gen thủ công ở process khác vẫn được notify bình thường
      const autoProcessId = (task.detail as any)?.payload?.processId || processId;
      if (autoProcessId) {
        localStorage.setItem('auto_process_media_running', autoProcessId);
      }
      const now = Date.now();
      if (now - lastPanelRefreshRef.current > 10_000) {
        lastPanelRefreshRef.current = now;
        refreshAllPanels();
        if (processId && scenesData[processId]) {
          scenesData[processId].forEach((scene: any) => fetchPanels(scene.id));
        }
      }
    }
  }, [refreshProcesses, refreshCharacters, refreshAllPanels, fetchScenes, fetchPanels, scenesData]);

  const getProcessIdFromTask = (task: AdminTask | null): string | null => {
    if (!task || !task.detail.subtasks) return null;
    const createProcessSubtask = task.detail.subtasks.find((s: any) => s.id === 'create_process');
    return createProcessSubtask?.result?.processId || null;
  };

  const handleUploadPanelImage = async (panel: SourcePanel, file: File) => {
    try {
      await sourceStoriesApi.uploadPanelImage(storyId, panel.sceneId, panel.id, file);
      message.success('Upload ảnh thành công');
      fetchPanels(panel.sceneId);
      refreshProcesses();
    } catch (error: any) {
      message.error('Upload ảnh thất bại: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSubmit = async (values: any) => {
    const { startChapterId, endChapterId, name } = values;
    
    // Find chapters in between
    const startIndex = chapters.findIndex(c => c.id === startChapterId);
    const endIndex = chapters.findIndex(c => c.id === endChapterId);
    
    if (startIndex === -1 || endIndex === -1) return;
    
    // Support both directions, but usually start < end
    const realStart = Math.min(startIndex, endIndex);
    const realEnd = Math.max(startIndex, endIndex);
    
    const chapterIds = chapters.slice(realStart, realEnd + 1).map(c => c.id);
    
    // Generate name if not provided
    let processName = name;
    if (!processName || !processName.trim()) {
      const startNum = realStart + 1;
      const endNum = realEnd + 1;
      processName = startNum === endNum ? `Chapter ${startNum} analysis` : `Chapters ${startNum}-${endNum} analysis`;
    }

    const payload: CreateSourceProcessRequest = {
      name: processName,
      chapterIds,
    };

    const result = await executeAsync(
      () => sourceStoriesApi.createSourceProcess(storyId, payload),
      { successMessage: 'Process created successfully!' }
    );

    if (result) {
      setIsModalVisible(false);
      form.resetFields();
      refreshProcesses();
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <Tag color="default">Draft</Tag>;
      case 'PROCESSING':
        return <Tag color="processing">Processing</Tag>;
      case 'COMPLETED':
        return <Tag color="success">Completed</Tag>;
      case 'FAILED':
        return <Tag color="error">Failed</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const renderIssueTag = (tag: string) => {
    const tagConfig: Record<string, { color: string; label: string }> = {
      CAPTION_TOO_LONG: { color: 'error', label: 'Caption dài' },
      CAPTION_TOO_SHORT: { color: 'warning', label: 'Caption ngắn' },
      MISSING_IDEAS: { color: 'volcano', label: 'Thiếu ý' },
      BAD_RHYTHM: { color: 'purple', label: 'Nhịp điệu' },
      CHARACTER_MISMATCH: { color: 'magenta', label: 'Nhân vật' },
      VISUAL_INCONSISTENCY: { color: 'orange', label: 'Hình ảnh' },
      OTHER: { color: 'default', label: 'Khác' },
    };

    const config = tagConfig[tag] || { color: 'default', label: tag };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const handleClearPanels = async (scene: SourceScene) => {
    Modal.confirm({
      title: 'Are you sure you want to clear all panels for this scene?',
      content: 'This action cannot be undone.',
      onOk: async () => {
        await executeAsync(
          () => sourceStoriesApi.clearSourcePanels(storyId, scene.id),
          { successMessage: 'Panels cleared successfully!' }
        );
        fetchPanels(scene.id);
        fetchScenes(scene.processId);
        refreshProcesses();
      }
    });
  };

  const handleReExtractPanels = (scene: SourceScene) => {
    setReExtractScene(scene);
    setReExtractComment('');
    setReExtractMediaStrategy('AUTO_MIX');
    setReExtractStep('input');
    setReExtractedPanels([]);
    setIsReExtractModalVisible(true);
  };

  const handlePreviewReExtract = async () => {
    if (!reExtractScene || !reExtractComment.trim()) {
      message.warning('Vui lòng nhập yêu cầu hiệu chỉnh');
      return;
    }

    const result = await executeAsync(
      () => sourceStoriesApi.previewReExtractPanels(storyId, reExtractScene.id, reExtractComment, reExtractMediaStrategy),
      { successMessage: 'AI đã tách lại xong, mời bạn review!' }
    );

    if (result && result.data) {
      setReExtractedPanels(result.data);
      setReExtractStep('preview');
    }
  };

  const handleConfirmReExtract = async () => {
    if (!reExtractScene || reExtractedPanels.length === 0) return;

    await executeAsync(
      () => sourceStoriesApi.confirmReExtractPanels(storyId, reExtractScene.id, reExtractedPanels),
      { successMessage: 'Đã cập nhật lại toàn bộ Panel!' }
    );

    setIsReExtractModalVisible(false);
    fetchPanels(reExtractScene.id);
    fetchScenes(reExtractScene.processId);
    refreshProcesses();
  };

  const handleReviewScene = async (scene: SourceScene) => {
    setReviewScene(scene);
    setReviewResult(null);
    setIsReviewModalVisible(true);

    const result = await executeAsync(
      () => sourceStoriesApi.reviewSceneContent(storyId, scene.id),
      { successMessage: 'AI đã review xong!' }
    );

    if (result && result.data) {
      setReviewResult(result.data);
    }
  };

  const handleApplyReviewSuggestion = () => {
    if (!reviewResult || !reviewScene) return;
    
    const suggestion = reviewResult.suggestedComment;
    setIsReviewModalVisible(false);
    
    // Trigger re-extract flow with suggestion
    setReExtractScene(reviewScene);
    setReExtractComment(suggestion);
    setReExtractStep('input');
    setReExtractedPanels([]);
    setIsReExtractModalVisible(true);
  };

  const handleClearScenes = async (process: SourceProcess) => {
    Modal.confirm({
      title: 'Are you sure you want to clear all scenes for this process?',
      content: 'This will also clear all panels within these scenes. This action cannot be undone.',
      onOk: async () => {
        await executeAsync(
          () => sourceStoriesApi.clearSourceScenes(storyId, process.id),
          { successMessage: 'Scenes cleared successfully!' }
        );
        fetchScenes(process.id);
      }
    });
  };

  const columns = [
    {
      title: 'Order',
      dataIndex: 'orderIndex',
      key: 'orderIndex',
      width: 80,
      render: (order: number) => <Tag color="blue">{order}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: SourceProcess) => {
        const { isComplete, completedCount, totalCount } = getProcessCompletionStats(record.id);
        const isActiveTask = getProcessIdFromTask(currentAutoTask) === record.id;
        const taskInProgress = currentAutoTask?.status === AdminTaskStatus.IN_PROGRESS;

        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {getStatusTag(status)}
                {isActiveTask && taskInProgress && (
                    <Tag color="processing" icon={<SyncOutlined spin />}>AUTO</Tag>
                )}
            </div>
            {isActiveTask && taskInProgress ? (
                <div style={{ marginTop: 4, width: 120 }}>
                    <Progress 
                        percent={currentAutoTask.detail.progress} 
                        size="small" 
                        status="active" 
                        strokeColor="#1890ff"
                    />
                </div>
            ) : (
                <Tag color={isComplete ? 'success' : 'warning'} style={{ marginTop: 4 }}>
                    {completedCount}/{totalCount} panels done
                </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Chapters',
      dataIndex: 'chapterIds',
      key: 'chapterIds',
      render: (chapterIds: string[]) => `${chapterIds.length} chapter(s)`,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: any, record: SourceProcess) => {
        const { isComplete } = getProcessCompletionStats(record.id);
        return (
          <Space>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
              title="View Details"
            />
            {record.status === 'DRAFT' && (
              <Tooltip title={!isComplete ? "All panels must have images to approve" : "Approve Process"}>
                <Button
                  type="text"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApproveProcess(record)}
                  title="Approve Process"
                  style={{ color: isComplete ? '#52c41a' : '#d9d9d9' }}
                  disabled={!isComplete}
                />
              </Tooltip>
            )}
            <Popconfirm
              title="Are you sure you want to delete this process?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                title="Delete"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const usedChapterIds = new Set<string>();
  processesData?.data.forEach(p => {
    p.chapterIds.forEach(id => usedChapterIds.add(id));
  });

  const availableChapters = chapters.filter(c => !usedChapterIds.has(c.id));

  // Check if we can create a new process
  const processes = processesData?.data || [];
  const latestProcess = processes.length > 0 
    ? [...processes].sort((a, b) => b.orderIndex - a.orderIndex)[0]
    : null;
  // const canCreateProcess = !latestProcess || latestProcess.status === 'COMPLETED';
  const canCreateProcess = true

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          minWidth: 0,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Process Management
        </Title>
        <Space wrap>
          <Button
            icon={<RobotOutlined />}
            onClick={handleAutoGenerate}
          >
            Tự động tạo Process
          </Button>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={handleBatchGenMedia}
            loading={batchGenLoading}
          >
            Batch Gen ảnh + video
          </Button>
          <Tooltip title={!canCreateProcess ? `Please approve the latest process (#${latestProcess?.orderIndex}) before creating a new one.` : ""}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              disabled={!canCreateProcess}
            >
              Create Process
            </Button>
          </Tooltip>
        </Space>
      </div>

      {autoGenerateTaskId && (
        <AdminTaskTracker
          taskId={autoGenerateTaskId}
          floating={true}
          onUpdate={handleTaskUpdate}
          onComplete={handleTaskComplete}
          onFail={handleTaskFail}
          onRestart={handleTaskRestart}
          onDelete={handleTaskDelete}
        />
      )}
      {pendingTaskCount > 0 && (
        <div style={{ marginBottom: 8, color: '#faad14' }}>
          {pendingTaskCount} process đang chờ trong hàng
        </div>
      )}

      <Table
        columns={columns}
        dataSource={processesData?.data || []}
        rowKey="id"
        loading={processesLoading && !processesData}
        expandable={{
          expandedRowKeys,
          onExpand: handleRowExpand,
          expandedRowRender: (record: SourceProcess) => {
            const scenes = scenesData[record.id] || [];
            const sLoading = scenesLoading[record.id];
            const processCharacterIds = record.characterIds || [];
            const processCharacters = storyCharacters.filter(c => processCharacterIds.includes(c.id));
            const charactersWithAvatar = processCharacters.filter(c => !!c.avatarUrl).length;
            const charactersLocalized = processCharacters.filter(c => (c.localizedData as any)?.name).length;
            const allCharactersLocalized = processCharacters.length > 0 && charactersLocalized === processCharacters.length;

            return (
              <div
                style={{
                  padding: '16px',
                  background: themeColors.bgTertiary,
                  minWidth: 0,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Card 
                  size="small" 
                  styles={{
                    header: { flexWrap: 'wrap', gap: 8, alignItems: 'center' },
                    extra: { marginInlineStart: 0 },
                  }}
                  title={
                    <Space>
                      <span>Characters Management</span>
                      {processCharacterIds.length > 0 && (
                        <>
                          <Tag color={charactersWithAvatar === processCharacterIds.length ? 'success' : 'warning'}>
                            Images: {charactersWithAvatar}/{processCharacterIds.length} done
                          </Tag>
                          <Tag color={allCharactersLocalized ? 'success' : 'warning'}>
                            Localized: {charactersLocalized}/{processCharacterIds.length} done
                          </Tag>
                        </>
                      )}
                    </Space>
                  } 
                  style={{ marginBottom: 16 }}
                  extra={
                    processCharacterIds.length > 0 && (
                      <Button
                        size="small"
                        icon={<RobotOutlined />}
                        onClick={() => handleExtractCharacters(record)}
                        loading={asyncLoading && selectedProcess?.id === record.id}
                      >
                        Re-extract Characters
                      </Button>
                    )
                  }
                >
                  {processCharacterIds.length === 0 ? (
                    <Empty
                      description="No characters associated with this process yet"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      <Button
                        type="primary"
                        icon={<RobotOutlined />}
                        onClick={() => handleExtractCharacters(record)}
                        loading={asyncLoading && selectedProcess?.id === record.id}
                      >
                        Extract Characters with AI
                      </Button>
                    </Empty>
                  ) : (
                    <List
                      grid={{ gutter: 16, column: 4 }}
                      dataSource={processCharacters}
                      renderItem={char => (
                        <List.Item>
                          <Card size="small" style={{ borderRadius: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div 
                                style={{ 
                                  width: 48, 
                                  height: 48, 
                                  borderRadius: '50%', 
                                  overflow: 'hidden', 
                                  background: themeColors.bgTertiary,
                                  border: `1px solid ${themeColors.bgTertiary}`,
                                  flexShrink: 0,
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleManageCharacterImage(char)}
                              >
                                {char.avatarUrl ? (
                                  <img 
                                    src={char.avatarUrl} 
                                    alt={char.name} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} 
                                  />
                                ) : (
                                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <RobotOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
                                  </div>
                                )}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontWeight: 'bold', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {char.name}
                                </div>
                                {(char.localizedData as any)?.name && (
                                  <div style={{ fontSize: 12, color: '#52c41a', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Thay thế: {(char.localizedData as any).name}
                                  </div>
                                )}
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                  {char.role}
                                </div>
                              </div>
                              <Button 
                                size="small" 
                                icon={<PictureOutlined />} 
                                onClick={() => handleManageCharacterImage(char)}
                                type={char.avatarUrl ? "default" : "dashed"}
                                style={!char.avatarUrl ? { color: '#1890ff', borderColor: '#1890ff' } : {}}
                              >
                                {char.avatarUrl ? 'Manage' : 'Create Image'}
                              </Button>
                            </div>
                          </Card>
                        </List.Item>
                      )}
                    />
                  )}
                </Card>

                {processCharacterIds.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    {scenes.length === 0 && sLoading ? (
                      <div style={{ textAlign: 'center', padding: '24px' }}>
                        <Spin tip="Loading scenes..." />
                      </div>
                    ) : (
                      <Spin spinning={sLoading}>
                        {scenes.length === 0 ? (
                          <Empty
                            description="No scenes extracted yet"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          >
                            <Tooltip title={!allCharactersLocalized ? "Tất cả nhân vật trong process phải có tên thay thế mới có thể Extract Scenes" : ""}>
                              <Button
                                type="primary"
                                icon={<RobotOutlined />}
                                onClick={() => handleExtractScenes(record)}
                                loading={currentExtractingProcessId === record.id}
                                disabled={!allCharactersLocalized}
                              >
                                Extract Scenes with AI
                              </Button>
                            </Tooltip>
                          </Empty>
                        ) : (
                          <>
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                marginBottom: 16,
                                minWidth: 0,
                              }}
                            >
                              <Title level={5} style={{ margin: 0 }}>
                                Scenes ({scenes.length})
                              </Title>
                              <Space wrap>
                                <Button
                                  icon={<PlayCircleOutlined />}
                                  onClick={() => handleAutoCreateMedia(record)}
                                >
                                  Auto Create Media
                                </Button>
                                <Button
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleClearScenes(record)}
                                >
                                  Clear Scenes
                                </Button>
                              </Space>
                            </div>
                            <Collapse 
                              accordion 
                              activeKey={activeSceneKeys[record.id]}
                              onChange={(key) => {
                                setActiveSceneKeys(prev => ({ ...prev, [record.id]: key }));
                                const sceneId = Array.isArray(key) ? key[0] : key;
                                if (sceneId) handleSceneExpand(sceneId as string);
                              }}
                            >
                              {scenes.map((scene, index) => {
                                const panels = panelsData[scene.id] || [];
                                const pLoading = panelsLoading[scene.id];
                                const doneCount = getSceneDoneCount(scene.id);
                                const totalPanels = scene.totalPanels || 0;
                                const isDone = doneCount === totalPanels && totalPanels > 0;

                                return (
                                  <Collapse.Panel
                                      header={
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: 8,
                                          width: '100%',
                                          maxWidth: '100%',
                                          minWidth: 0,
                                          paddingRight: 24,
                                          boxSizing: 'border-box',
                                        }}
                                      >
                                        <Space wrap size={[4, 8]} style={{ minWidth: 0, flex: '1 1 200px' }}>
                                          <Tag color="blue">#{scene.orderIndex}</Tag>
                                          <Text strong style={{ wordBreak: 'break-word' }}>{scene.title}</Text>
                                          <Tag color={isDone ? 'success' : 'warning'}>
                                            {doneCount}/{totalPanels} panels done
                                          </Tag>
                                          {(() => {
                                            const outfitStatus = getOutfitStatus(scene.id);
                                            return (
                                              <Tag color={outfitStatus.color} icon={<SkinOutlined />}>
                                                Outfits: {outfitStatus.text}
                                              </Tag>
                                            );
                                          })()}
                                          {scene.reason && (
                                            <Tag color="green" style={{ marginLeft: 8 }}>
                                              {scene.reason.substring(0, 50)}...
                                            </Tag>
                                          )}
                                        </Space>
                                        <Space wrap size="small" onClick={e => e.stopPropagation()} style={{ flex: '0 1 auto', maxWidth: '100%' }}>
                                          {totalPanels === 0 && (
                                            <Tooltip title={!allCharactersLocalized ? "Tất cả nhân vật trong process phải có tên thay thế mới có thể Extract Panels" : ""}>
                                              <Button
                                                size="small"
                                                type="primary"
                                                icon={<RobotOutlined />}
                                                onClick={() => handleExtractPanels(scene)}
                                                loading={currentExtractingSceneId === scene.id}
                                                disabled={!allCharactersLocalized}
                                              >
                                                Extract Panels
                                              </Button>
                                            </Tooltip>
                                          )}
                                          <Tooltip title={
                                            totalPanels === 0 
                                              ? "Vui lòng extract panels cho scene này trước" 
                                              : "Quản lý ảnh hàng loạt cho scene này"
                                          }>
                                            <Button
                                              size="small"
                                              type="dashed"
                                              icon={<RobotOutlined />}
                                              onClick={() => {
                                                setSelectedSceneForBulk(scene);
                                                setIsBulkModalVisible(true);
                                              }}
                                              disabled={totalPanels === 0}
                                              style={totalPanels > 0 
                                                ? (isDone ? { color: '#1890ff', borderColor: '#1890ff' } : { color: '#722ed1', borderColor: '#722ed1' }) 
                                                : {}
                                              }
                                            >
                                              Quản lý panel
                                            </Button>
                                          </Tooltip>
                                          <Tooltip title={totalPanels === 0 ? "Vui lòng extract panels trước khi phân tích outfits" : generatingRefImageId !== null ? "Đang gen ảnh outfit, vui lòng chờ..." : "AI phân tích trang phục nhân vật trong scene (dựa trên panels hiện có)"}>
                                            <Button
                                              size="small"
                                              icon={<SkinOutlined />}
                                              onClick={() => handlePlanOutfits(scene)}
                                              loading={planningOutfitSceneId === scene.id}
                                              disabled={totalPanels === 0 || generatingRefImageId !== null}
                                            >
                                              Outfits
                                            </Button>
                                          </Tooltip>
                                          <Tooltip title={scene.totalPanels === 0 ? "Vui lòng extract panels trước khi review" : "AI Review chất lượng nội dung các Panel hiện tại"}>
                                            <Button
                                              size="small"
                                              icon={<AuditOutlined />}
                                              onClick={() => handleReviewScene(scene)}
                                              disabled={!scene.totalPanels || scene.totalPanels === 0}
                                            >
                                              AI Review
                                            </Button>
                                          </Tooltip>
                                          <Tooltip title={
                                            !allCharactersLocalized 
                                              ? "Tất cả nhân vật trong process phải có tên thay thế mới có thể Re-extract" 
                                              : "Yêu cầu AI tách lại toàn bộ Panel cho scene này dựa trên ý kiến của bạn"
                                          }>
                                            <Button
                                              size="small"
                                              icon={<ThunderboltOutlined />}
                                              onClick={() => handleReExtractPanels(scene)}
                                              disabled={!allCharactersLocalized}
                                            >
                                              Re-extract
                                            </Button>
                                          </Tooltip>
                                        </Space>
                                      </div>
                                    }
                                    key={scene.id}
                                  >
                                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                      <Row gutter={[16, 16]}>
                                        <Col span={24}>
                                          <Card size="small" title="Scene Overview" headStyle={{ background: themeColors.bgSecondary }}>
                                            <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                              {scene.summary && (
                                                <div>
                                                  <Text strong>Summary: </Text>
                                                  <Text>{scene.summary}</Text>
                                                </div>
                                              )}
                                              {scene.setting && (
                                                <div>
                                                  <Text strong>Setting: </Text>
                                                  <Text>{scene.setting}</Text>
                                                </div>
                                              )}
                                              {scene.reason && (
                                                <div>
                                                  <Text strong>Why it matters: </Text>
                                                  <Text italic style={{ color: '#8c8c8c' }}>{scene.reason}</Text>
                                                </div>
                                              )}
                                              {scene.characterIds && (scene.characterIds as string[]).length > 0 && (
                                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                                  <Text strong>Characters: </Text>
                                                  {(scene.characterIds as string[]).map(id => (
                                                    <Tag key={id} style={{ margin: 0 }} color="blue">{getCharacterName(id)}</Tag>
                                                  ))}
                                                </div>
                                              )}
                                            </Space>
                                          </Card>
                                        </Col>
                                        {/* Epic 4: Outfit References */}
                                        <Col span={24}>
                                          <Collapse ghost onChange={(key) => {
                                            if (key.includes('outfits') && !outfitRefsData[scene.id]) {
                                              loadOutfitRefs(scene.id);
                                            }
                                          }}>
                                            <Collapse.Panel
                                              header={
                                                <Space>
                                                  <SkinOutlined />
                                                  <Text strong>Character Outfits</Text>
                                                  {outfitRefsData[scene.id] && (
                                                    <Tooltip title="Outfits marked 'Changed' need new reference images. 'Unchanged' outfits reuse previous scene's image.">
                                                      <Tag>{outfitRefsData[scene.id].filter((r: SceneCharacterReference) => !r.isDefaultOutfit).length} changed</Tag>
                                                    </Tooltip>
                                                  )}
                                                </Space>
                                              }
                                              key="outfits"
                                            >
                                              {outfitRefsLoading[scene.id] ? (
                                                <Spin size="small" />
                                              ) : !outfitRefsData[scene.id] || outfitRefsData[scene.id].length === 0 ? (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                  No outfit data. Click "Outfits" button to AI-plan outfits for this scene.
                                                </Text>
                                              ) : (
                                                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                                                  {(outfitRefsData[scene.id] as SceneCharacterReference[]).map((ref) => (
                                                    <div key={ref.id} style={{
                                                      display: 'flex', alignItems: 'flex-start', gap: 12,
                                                      padding: '8px 12px', background: ref.isDefaultOutfit ? themeColors.bgSecondary : themeColors.bgSuccess,
                                                      borderRadius: 6, border: `1px solid ${ref.isDefaultOutfit ? themeColors.bgTertiary : themeColors.borderSuccess}`,
                                                      color: themeColors.textPrimary,
                                                      minWidth: 0,
                                                    }}>
                                                      {ref.referenceUrl ? (
                                                        <Image 
                                                          src={ref.referenceUrl} 
                                                          alt="outfit" 
                                                          style={{ width: 40, height: 56, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                                                          preview={{
                                                            mask: <EyeOutlined style={{ fontSize: 14 }} />
                                                          }}
                                                        />
                                                      ) : (
                                                        <div style={{ width: 40, height: 56, background: themeColors.bgTertiary, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                          <SkinOutlined style={{ color: themeColors.textTertiary }} />
                                                        </div>
                                                      )}
                                                      <div style={{ flex: 1, minWidth: 0 }}>
                                                        <Space size={4}>
                                                          <Text strong style={{ fontSize: 12 }}>{getCharacterName(ref.characterId)}</Text>
                                                          <Tooltip
                                                            title={
                                                              ref.isDefaultOutfit
                                                                ? 'Unchanged: Outfit matches previous scene → reuses previous reference image (no generation needed).'
                                                                : 'Changed: New or different outfit → requires generating new reference image (Scene 1 always "Changed").'
                                                            }
                                                          >
                                                            <Tag color={ref.isDefaultOutfit ? 'default' : 'orange'} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', cursor: 'help' }}>
                                                              {ref.isDefaultOutfit ? 'Unchanged' : 'Changed'}
                                                            </Tag>
                                                          </Tooltip>
                                                        </Space>
                                                        <div style={{ marginTop: 4 }}>
                                                          <Text
                                                            type="secondary"
                                                            style={{
                                                              fontSize: 11,
                                                              whiteSpace: 'normal',
                                                              wordBreak: 'break-word',
                                                              display: 'block',
                                                            }}
                                                          >
                                                            {ref.outfitDescription || 'No description'}
                                                          </Text>
                                                        </div>
                                                      </div>
                                                      <Space size={4} style={{ flexShrink: 0 }}>
                                                        {!ref.referenceUrl && (
                                                          <Tooltip title={planningOutfitSceneId !== null ? 'Đang plan outfits, vui lòng chờ...' : ref.isDefaultOutfit ? 'Populate with previous scene / checkpoint / avatar image' : 'Generate new reference image for changed outfit'}>
                                                            <Button
                                                              type="text" size="small"
                                                              icon={<PictureOutlined />}
                                                              onClick={() => handleGenRefImage(ref.id, scene.id)}
                                                              loading={generatingRefImageId === ref.id}
                                                              disabled={planningOutfitSceneId !== null}
                                                            />
                                                          </Tooltip>
                                                        )}
                                                        {ref.referenceUrl && ref.isDefaultOutfit && (
                                                          <Tooltip title="Force regenerate: Generate new image even though outfit is marked as unchanged (useful if you updated the outfit description)">
                                                            <Button
                                                              type="text" size="small"
                                                              icon={<SyncOutlined />}
                                                              onClick={() => handleGenRefImage(ref.id, scene.id, true)}
                                                              loading={generatingRefImageId === ref.id}
                                                              disabled={planningOutfitSceneId !== null}
                                                              danger
                                                            />
                                                          </Tooltip>
                                                        )}
                                                        <Tooltip title="Upload outfit image manually">
                                                          <Button
                                                            type="text" size="small"
                                                            icon={<UploadOutlined />}
                                                            loading={uploadingOutfitRefId === ref.id}
                                                            disabled={planningOutfitSceneId !== null}
                                                            onClick={() => {
                                                              const input = document.createElement('input');
                                                              input.type = 'file';
                                                              input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                                                              input.onchange = (e) => {
                                                                const file = (e.target as HTMLInputElement).files?.[0];
                                                                if (file) handleUploadOutfitImage(ref.id, scene.id, file);
                                                              };
                                                              input.click();
                                                            }}
                                                          />
                                                        </Tooltip>
                                                        <Tooltip title="Edit outfit description">
                                                          <Button
                                                            type="text" size="small"
                                                            icon={<EditOutlined />}
                                                            onClick={() => handleEditOutfitRef(ref)}
                                                          />
                                                        </Tooltip>
                                                        {!ref.isDefaultOutfit && (
                                                          <Popconfirm title="Delete this outfit reference?" onConfirm={() => handleDeleteOutfitRef(ref.id, scene.id)}>
                                                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                                                          </Popconfirm>
                                                        )}
                                                      </Space>
                                                    </div>
                                                  ))}
                                                </Space>
                                              )}
                                            </Collapse.Panel>
                                          </Collapse>
                                        </Col>
                                        {(scene.keyMoments?.length || scene.endState) && (
                                          <Col span={24}>
                                            <Collapse 
                                              ghost 
                                              activeKey={activeDetailKeys[scene.id]}
                                              onChange={(key) => setActiveDetailKeys(prev => ({ ...prev, [scene.id]: key }))}
                                            >
                                              <Collapse.Panel header={<Text strong>Key Moments & End State</Text>} key="details">
                                                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                                                  {scene.keyMoments && scene.keyMoments.length > 0 && (
                                                    <div>
                                                      <Text strong>Key Moments:</Text>
                                                      <div style={{ marginTop: 4 }}>
                                                        {scene.keyMoments.map((moment, i) => (
                                                          <Tag key={i} color="cyan" style={{ marginBottom: 4 }}>{moment}</Tag>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                  {scene.endState && (
                                                    <div>
                                                      <Text strong>End State:</Text>
                                                      <div style={{
                                                        marginTop: 4,
                                                        padding: '8px 12px',
                                                        background: themeColors.bgSuccess,
                                                        borderRadius: '4px',
                                                        borderLeft: `3px solid ${themeColors.borderSuccess}`,
                                                        color: themeColors.textPrimary
                                                      }}>
                                                        {scene.endState}
                                                      </div>
                                                    </div>
                                                  )}
                                                </Space>
                                              </Collapse.Panel>
                                            </Collapse>
                                          </Col>
                                        )}
                                      </Row>
                                      
                                      <Divider style={{ margin: '8px 0' }} />
                                      
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: 8,
                                          minWidth: 0,
                                        }}
                                      >
                                        <Title level={5} style={{ margin: 0 }}>
                                          <PictureOutlined style={{ marginRight: 8 }} />
                                          Panels Retelling
                                        </Title>
                                        <Space wrap>
                                          {panels.length > 0 && (
                                            <Button
                                              danger
                                              size="small"
                                              icon={<DeleteOutlined />}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleClearPanels(scene);
                                              }}
                                            >
                                              Clear Panels
                                            </Button>
                                          )}
                                        </Space>
                                      </div>

                                      {panels.length === 0 && pLoading ? (
                                        <div style={{ textAlign: 'center', padding: '16px' }}>
                                          <Spin size="small" tip="Loading panels..." />
                                        </div>
                                      ) : (
                                        <Spin spinning={pLoading}>
                                          {panels.length === 0 ? (
                                            <Empty description="No panels extracted yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                          ) : (
                                            <List
                                              dataSource={panels}
                                              renderItem={(panel) => (
                                                <List.Item>
                                                  <Card size="small" style={{ width: '100%', maxWidth: '100%', borderLeft: '4px solid #1890ff' }}>
                                                    <Row gutter={16} align="top" wrap>
                                                      <Col flex="auto" style={{ minWidth: 0 }}>
                                                        <Space direction="vertical" style={{ width: '100%', maxWidth: '100%' }}>
                                                          <div
                                                            style={{
                                                              display: 'flex',
                                                              flexWrap: 'wrap',
                                                              alignItems: 'center',
                                                              justifyContent: 'space-between',
                                                              gap: 8,
                                                              minWidth: 0,
                                                            }}
                                                          >
                                                            <Space wrap size="small">
                                                              <Tag color="blue">Panel #{panel.sceneOrderIndex}</Tag>
                                                              {panel.mediaType === 'VIDEO' ? (
                                                                <Tag color="purple" icon={<VideoCameraOutlined />}>VIDEO</Tag>
                                                              ) : (
                                                                <Tag color="cyan" icon={<PictureOutlined />}>IMAGE</Tag>
                                                              )}
                                                              <Tag color="default">{countWords(panel.localizedCaption || panel.caption)} từ</Tag>
                                                              {panel.estimatedAudioDuration && (
                                                                <Tag color="orange">~{panel.estimatedAudioDuration}s audio</Tag>
                                                              )}
                                                              <Text italic style={{ color: '#555' }}> Storyteller retelling:</Text>
                                                            </Space>
                                                            <Space wrap size="small">
                                                              <Button 
                                                                size="small" 
                                                                icon={<PictureOutlined />}
                                                                onClick={() => handleManagePanelImage(panel)}
                                                              >
                                                                {panel.imagePath ? 'Manage' : 'Generate'}
                                                              </Button>
                                                              <Upload
                                                                accept="image/*"
                                                                showUploadList={false}
                                                                beforeUpload={(file) => {
                                                                  handleUploadPanelImage(panel, file);
                                                                  return false;
                                                                }}
                                                              >
                                                                <Button 
                                                                  size="small" 
                                                                  icon={<UploadOutlined />} 
                                                                >
                                                                  Upload
                                                                </Button>
                                                              </Upload>
                                                              <Tooltip title="Hiệu chỉnh nội dung bằng AI">
                                                                <Button
                                                                  size="small"
                                                                  icon={<ThunderboltOutlined />}
                                                                  onClick={() => handleAiRefinePanel(panel)}
                                                                />
                                                              </Tooltip>
                                                              <Button
                                                                size="small"
                                                                type="text"
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => handleDeletePanel(panel)}
                                                              />
                                                            </Space>
                                                          </div>
                                                          <div style={{ padding: '8px', background: themeColors.bgSuccess, borderRadius: 4, border: `1px solid ${themeColors.borderSuccess}`, color: themeColors.textPrimary }}>
                                                            <Text strong>"{panel.localizedCaption || panel.caption}"</Text>
                                                          </div>
                                                          <div>
                                                            <Text type="secondary" style={{ fontSize: '12px' }}>Visual Description:</Text>
                                                            <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                                                              {panel.visualDescription}
                                                            </div>
                                                          </div>
                                                          {panel.characterIds && panel.characterIds.length > 0 && (
                                                            <div>
                                                              {panel.characterIds.map(id => (
                                                                <Tag key={id} color="blue">{getCharacterName(id)}</Tag>
                                                              ))}
                                                            </div>
                                                          )}
                                                        </Space>
                                                      </Col>
                                                      {(panel.videoUrl || panel.imagePath) && (
                                                        <Col flex="120px">
                                                          <div style={{ width: '120px', aspectRatio: '9 / 16', overflow: 'hidden', borderRadius: 4, border: '1px solid #f0f0f0', background: '#000' }}>
                                                            {panel.videoUrl ? (
                                                              <video
                                                                key={panel.videoUrl}
                                                                src={panel.videoUrl}
                                                                poster={panel.firstFrameUrl || panel.imagePath || undefined}
                                                                controls
                                                                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                                              />
                                                            ) : (
                                                              <img
                                                                src={panel.imagePath}
                                                                alt="Panel"
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                                                                onClick={() => handleManagePanelImage(panel)}
                                                              />
                                                            )}
                                                          </div>
                                                        </Col>
                                                      )}
                                                    </Row>
                                                  </Card>
                                                </List.Item>
                                              )}
                                            />
                                          )}
                                        </Spin>
                                      )}
                                    </Space>
                                  </Collapse.Panel>
                                );
                              })}
                            </Collapse>
                          </>
                        )}
                      </Spin>
                    )}
                  </div>
                )}
              </div>
            );
          },
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <DownOutlined
                rotate={180}
                onClick={e => onExpand(record, e)}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <DownOutlined onClick={e => onExpand(record, e)} style={{ cursor: 'pointer' }} />
            ),
        }}
      />

      {/* Create Process Modal */}
      <Modal
        title="Create New Process"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Process Name"
          >
            <Input placeholder="Leave blank for auto-generation (e.g. Chapters 1-5 analysis)" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startChapterId"
                label="From Chapter"
                rules={[{ required: true, message: 'Please select start chapter' }]}
              >
                <Select placeholder="Select start" showSearch optionFilterProp="children">
                  {availableChapters.map((chapter) => {
                    const originalIndex = chapters.findIndex(c => c.id === chapter.id);
                    return (
                      <Option key={chapter.id} value={chapter.id}>
                        {chapter.name || `Chapter ${originalIndex + 1}`}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endChapterId"
                label="To Chapter"
                rules={[{ required: true, message: 'Please select end chapter' }]}
              >
                <Select placeholder="Select end" showSearch optionFilterProp="children">
                  {availableChapters.map((chapter) => {
                    const originalIndex = chapters.findIndex(c => c.id === chapter.id);
                    return (
                      <Option key={chapter.id} value={chapter.id}>
                        {chapter.name || `Chapter ${originalIndex + 1}`}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Process Detail Modal */}
      <Modal
        title={`Process: ${selectedProcess?.name}`}
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedProcess && (() => {
          const processCharacterIds = selectedProcess.characterIds || [];
          const processCharacters = storyCharacters.filter(c => processCharacterIds.includes(c.id));
          const charactersWithAvatar = processCharacters.filter(c => !!c.avatarUrl).length;
          const charactersLocalized = processCharacters.filter(c => (c.localizedData as any)?.name).length;
          const allCharactersLocalized = processCharacters.length > 0 && charactersLocalized === processCharacters.length;
          
          return (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Row gutter={24}>
                <Col span={12}>
                  <Text strong>Status: </Text>
                  {getStatusTag(selectedProcess.status)}
                </Col>
                <Col span={12}>
                  <Text strong>Created: </Text>
                  {formatDate(selectedProcess.createdAt)}
                </Col>
              </Row>

              <Card 
                size="small" 
                title={
                  <Space>
                    <span>Characters</span>
                    {processCharacterIds.length > 0 && (
                      <>
                        <Tag color={charactersWithAvatar === processCharacterIds.length ? 'success' : 'warning'}>
                          Images: {charactersWithAvatar}/{processCharacterIds.length} done
                        </Tag>
                        <Tag color={allCharactersLocalized ? 'success' : 'warning'}>
                          Localized: {charactersLocalized}/{processCharacterIds.length} done
                        </Tag>
                      </>
                    )}
                  </Space>
                }
                extra={
                  <Button
                    size="small"
                    icon={<RobotOutlined />}
                    onClick={() => handleExtractCharacters(selectedProcess)}
                    loading={asyncLoading && selectedProcess?.id === selectedProcess.id}
                  >
                    Re-extract
                  </Button>
                }
              >
                {processCharacters.length === 0 ? (
                  <Empty description="No characters" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <List
                    grid={{ gutter: 12, column: 2 }}
                    dataSource={processCharacters}
                    renderItem={char => (
                      <List.Item style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8, border: '1px solid #f0f0f0', borderRadius: 8, width: '100%' }}>
                          <div 
                            style={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              overflow: 'hidden', 
                              background: themeColors.bgTertiary,
                              flexShrink: 0,
                              cursor: 'pointer'
                            }}
                            onClick={() => handleManageCharacterImage(char)}
                          >
                            {char.avatarUrl ? (
                              <img src={char.avatarUrl} alt={char.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <RobotOutlined style={{ fontSize: 20, color: '#bfbfbf' }} />
                              </div>
                            )}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 'bold', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {char.name}
                            </div>
                            {(char.localizedData as any)?.name && (
                              <div style={{ fontSize: 11, color: '#52c41a', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                Thay thế: {(char.localizedData as any).name}
                              </div>
                            )}
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                              {char.role}
                            </div>
                          </div>
                          <Button 
                            size="small" 
                            icon={<PictureOutlined />} 
                            onClick={() => handleManageCharacterImage(char)}
                            type={char.avatarUrl ? "default" : "dashed"}
                            style={{ 
                              fontSize: '11px',
                              ...( !char.avatarUrl ? { color: '#1890ff', borderColor: '#1890ff' } : {})
                            }}
                          >
                            {char.avatarUrl ? 'Manage' : 'Create'}
                          </Button>
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </Card>
              
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Included Chapters:</Text>
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                  <List
                    size="small"
                    dataSource={selectedProcess.chapterIds}
                    renderItem={(chapterId) => {
                      const chapter = chapters.find(c => c.id === chapterId);
                      return (
                        <List.Item>
                          {chapter?.name || `Unknown Chapter (${chapterId})`}
                        </List.Item>
                      );
                    }}
                  />
                </div>
              </div>

              {selectedProcess.summary && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>AI Summary:</Text>
                  <div style={{ backgroundColor: themeColors.bgSecondary, padding: '12px', borderRadius: '4px', whiteSpace: 'pre-wrap', fontSize: '13px', color: themeColors.textPrimary }}>
                    {selectedProcess.summary}
                  </div>
                </div>
              )}
            </Space>
          );
        })()}
      </Modal>

      {/* AI Extraction Results Modal */}
      <Modal
        title={`AI Extracted Characters - ${selectedProcess?.name}`}
        open={isExtractModalVisible}
        onCancel={() => setIsExtractModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setIsExtractModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            icon={<CheckCircleOutlined />} 
            onClick={handleImportCharacters}
            loading={asyncLoading}
          >
            Save Characters and Update Process
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            AI has detected the following characters in this process. 
            New characters will be created, and all listed characters will be associated with this process.
          </Text>
        </div>
        <Table
          dataSource={extractedCharacters}
          rowKey={(_, index) => index!.toString()}
          pagination={false}
          columns={[
            {
              title: 'Character',
              key: 'name',
              render: (_, record) => (
                <Space>
                  <Tag color={record.isNew ? 'green' : 'blue'}>
                    {record.isNew ? 'NEW' : 'EXISTING'}
                  </Tag>
                  <Text strong>{record.name}</Text>
                </Space>
              ),
            },
            {
              title: 'Role',
              dataIndex: 'role',
              key: 'role',
              render: (role) => <Tag>{role}</Tag>,
            },
            {
              title: 'AI Description',
              dataIndex: 'description',
              key: 'description',
              ellipsis: true,
            },
          ]}
        />
      </Modal>

      {/* Scene Preview Modal */}
      <Modal
        title={`AI Extracted Scenes - ${selectedProcess?.name}`}
        open={isScenePreviewModalVisible}
        onCancel={() => setIsScenePreviewModalVisible(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setIsScenePreviewModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            icon={<CheckCircleOutlined />} 
            onClick={handleSaveScenes}
            loading={asyncLoading}
          >
            Save All Scenes
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            AI has extracted <Text strong>{extractedScenes.length}</Text> important scenes. 
            Review them below and click "Save All Scenes" to proceed.
          </Text>
        </div>
        <Collapse accordion defaultActiveKey={['0']}>
          {extractedScenes.map((scene, index) => (
            <Collapse.Panel
              header={
                <Space>
                  <Tag color="blue">#{index + 1}</Tag>
                  <Text strong>{scene.title}</Text>
                  {scene.reason && (
                    <Tag color="green" style={{ marginLeft: 8 }}>
                      Important: {scene.reason.substring(0, 40)}...
                    </Tag>
                  )}
                </Space>
              }
              key={index.toString()}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text strong>Summary:</Text>
                  <div style={{ marginTop: 4, padding: 8, background: themeColors.bgSecondary, borderRadius: 4, color: themeColors.textPrimary }}>
                    {scene.summary}
                  </div>
                </div>
                <div>
                  <Text strong>Setting:</Text>
                  <div style={{ marginTop: 4, padding: 8, background: themeColors.bgSecondary, borderRadius: 4, color: themeColors.textPrimary }}>
                    {scene.setting}
                  </div>
                </div>
                <div>
                  <Text strong>Characters: </Text>
                  <div style={{ marginTop: 4 }}>
                    {scene.characterIds.map(id => (
                      <Tag key={id} color="blue">{getCharacterName(id)}</Tag>
                    ))}
                  </div>
                </div>
                <div>
                  <Text strong>Key Moments:</Text>
                  <div style={{ marginTop: 4 }}>
                    {scene.keyMoments?.map((moment, i) => (
                      <Tag key={i} color="cyan" style={{ marginBottom: 4 }}>{moment}</Tag>
                    ))}
                  </div>
                </div>
                <div>
                  <Text strong>End State:</Text>
                  <div style={{ marginTop: 4, padding: 8, background: themeColors.bgSuccess, borderRadius: 4, borderLeft: `3px solid ${themeColors.borderSuccess}`, color: themeColors.textPrimary }}>
                    {scene.endState}
                  </div>
                </div>
                <div>
                  <Text strong>Why this scene matters:</Text>
                  <div style={{ marginTop: 4, padding: 8, background: themeColors.bgWarning, borderRadius: 4, borderLeft: `3px solid ${themeColors.borderWarning}`, color: themeColors.textPrimary }}>
                    {scene.reason}
                  </div>
                </div>
              </Space>
            </Collapse.Panel>
          ))}
        </Collapse>
      </Modal>

      {/* Panel Preview Modal */}
      <Modal
        title={`AI Extracted Panels - Scene: ${selectedSceneForPanels?.title}`}
        open={isPanelPreviewModalVisible}
        onCancel={() => setIsPanelPreviewModalVisible(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setIsPanelPreviewModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            icon={<CheckCircleOutlined />} 
            onClick={handleSavePanels}
            loading={asyncLoading}
          >
            Save All Panels
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            AI has break down this scene into <Text strong>{extractedPanels.length}</Text> panels for retelling. 
            Each panel includes a storyteller caption and a visual description.
          </Text>
        </div>
        <List
          dataSource={extractedPanels}
          renderItem={(panel, index) => (
            <List.Item>
              <Card 
                size="small" 
                style={{ 
                  width: '100%', 
                  borderLeft: `4px solid ${panel.isVideo ? '#722ed1' : '#52c41a'}` 
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Tag color="green">New Panel #{index + 1}</Tag>
                    <Tag color="default">{countWords(panel.caption)} từ</Tag>
                    {panel.isVideo ? (
                      <Tag color="purple" icon={<VideoCameraOutlined />}>VIDEO</Tag>
                    ) : (
                      <Tag color="cyan" icon={<PictureOutlined />}>IMAGE</Tag>
                    )}
                    {panel.estimatedAudioDuration && (
                      <Tag color="orange">~{panel.estimatedAudioDuration}s audio</Tag>
                    )}
                  </div>
                  <div style={{ padding: '8px', background: themeColors.bgSuccess, borderRadius: 4, border: `1px solid ${themeColors.borderSuccess}`, color: themeColors.textPrimary }}>
                    <Text strong>"{panel.localizedCaption || panel.caption}"</Text>
                  </div>
                  <div>
                    <Text strong>Visual Description:</Text>
                    <div style={{ marginTop: 4 }}>
                      {panel.visualDescription}
                    </div>
                  </div>
                  {panel.characterIds && panel.characterIds.length > 0 && (
                    <div>
                      <Text type="secondary" style={{ marginRight: 8 }}>Characters:</Text>
                      {panel.characterIds.map(id => (
                        <Tag key={id} color="blue">{getCharacterName(id)}</Tag>
                      ))}
                    </div>
                  )}
                </Space>
              </Card>
            </List.Item>
          )}
        />
      </Modal>
      
      {selectedPanel && (
        <PanelImageModal
          visible={isPanelImageModalVisible}
          panel={selectedPanel}
          storyId={storyId}
          getCharacterName={getCharacterName}
          onRefresh={(updatedPanel) => {
            // Update the local panels data with the refreshed panel
            const sceneId = updatedPanel.sceneId;
            setPanelsData(prev => {
              const currentScenePanels = prev[sceneId] || [];
              const updatedPanels = currentScenePanels.map(p => 
                p.id === updatedPanel.id ? updatedPanel : p
              );
              return { ...prev, [sceneId]: updatedPanels };
            });
            setSelectedPanel(updatedPanel);
          }}
          onClose={() => {
            setIsPanelImageModalVisible(false);
            const sceneId = selectedPanel.sceneId;
            const processId = selectedPanel.processId;
            setSelectedPanel(null);
            // Refresh panels for the scene to show updated image
            if (sceneId) {
              fetchPanels(sceneId);
            }
            // Also refresh scenes to update the panel count in the header
            if (processId) {
              fetchScenes(processId);
              // Refresh processes to update content completion status
              refreshProcesses();
              refreshAllPanels();
            }
          }}
        />
      )}

      {selectedSceneForBulk && (
        <BulkPanelModal
          visible={isBulkModalVisible}
          scene={selectedSceneForBulk}
          storyId={storyId}
          onRefresh={() => {
            fetchPanels(selectedSceneForBulk.id);
            fetchScenes(selectedSceneForBulk.processId);
            refreshProcesses();
            refreshAllPanels();
          }}
          onClose={() => {
            setIsBulkModalVisible(false);
            const processId = selectedSceneForBulk.processId;
            const sceneId = selectedSceneForBulk.id;
            setSelectedSceneForBulk(null);
            if (processId) {
              fetchPanels(sceneId);
              fetchScenes(processId);
              refreshProcesses();
              refreshAllPanels();
            }
          }}
        />
      )}

      {/* Re-extract Panels Modal */}
      <Modal
        title={`AI Re-extract Panels - Scene: ${reExtractScene?.title}`}
        open={isReExtractModalVisible}
        onCancel={() => setIsReExtractModalVisible(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setIsReExtractModalVisible(false)}>
            Hủy
          </Button>,
          reExtractStep === 'input' ? (
            <Button 
              key="preview" 
              type="primary" 
              icon={<ThunderboltOutlined />} 
              onClick={handlePreviewReExtract}
              loading={asyncLoading}
            >
              Gửi yêu cầu cho AI
            </Button>
          ) : (
            <Button 
              key="confirm" 
              type="primary" 
              danger
              icon={<CheckCircleOutlined />} 
              onClick={handleConfirmReExtract}
              loading={asyncLoading}
            >
              Xác nhận & Lưu (Sẽ xóa panel cũ)
            </Button>
          ),
          reExtractStep === 'preview' && (
            <Button key="back" onClick={() => setReExtractStep('input')}>
              Quay lại nhập comment
            </Button>
          )
        ]}
      >
        {reExtractStep === 'input' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              message="Lưu ý"
              description="Hành động này sẽ yêu cầu AI tách lại toàn bộ các panel cho scene này. Sau khi bạn xác nhận lưu, các panel cũ và toàn bộ ảnh đã tạo của chúng sẽ bị xóa vĩnh viễn."
              type="warning"
              showIcon
            />
            <div>
              <Text strong>Chiến lược Media:</Text>
              <Select
                value={reExtractMediaStrategy}
                onChange={(val) => setReExtractMediaStrategy(val)}
                style={{ width: '100%', marginTop: 8 }}
                options={[
                  {
                    value: 'AUTO_MIX',
                    label: (
                      <Space>
                        <Tag color="purple">🤖 Auto Mix</Tag>
                        <Text>AI tự quyết định panel nào là Video, panel nào là Ảnh</Text>
                      </Space>
                    ),
                  },
                  {
                    value: 'ALL_IMAGE',
                    label: (
                      <Space>
                        <Tag color="cyan">🖼️ All Image</Tag>
                        <Text>Toàn bộ panels là ảnh tĩnh (như cũ)</Text>
                      </Space>
                    ),
                  },
                  {
                    value: 'ALL_VIDEO',
                    label: (
                      <Space>
                        <Tag color="volcano">🎬 All Video</Tag>
                        <Text>Toàn bộ panels sẽ là video</Text>
                      </Space>
                    ),
                  },
                ]}
              />
            </div>
            <div style={{ marginTop: 16 }}>
              <Text strong>Yêu cầu của bạn:</Text>
              <Input.TextArea
                rows={6}
                placeholder="Ví dụ: Hãy tách thành nhiều panel hơn để mô tả kỹ tâm trạng nhân vật, hoặc gộp bớt các panel hội thoại lại..."
                value={reExtractComment}
                onChange={(e) => setReExtractComment(e.target.value)}
                style={{ marginTop: 8 }}
              />
            </div>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              message="Review kết quả từ AI"
              description="Dưới đây là danh sách panel mới mà AI gợi ý. Hãy kiểm tra kỹ trước khi bấm 'Xác nhận & Lưu'."
              type="info"
              showIcon
            />
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <List
                dataSource={reExtractedPanels}
                renderItem={(panel, index) => (
                  <List.Item>
                    <Card size="small" style={{ width: '100%', borderLeft: '4px solid #faad14' }}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Space>
                          <Tag color="orange">Gợi ý Panel #{index + 1}</Tag>
                          {panel.isVideo ? (
                            <Tag color="purple" icon={<VideoCameraOutlined />}>VIDEO</Tag>
                          ) : (
                            <Tag color="cyan" icon={<PictureOutlined />}>IMAGE</Tag>
                          )}
                          <Tag color="default">{countWords(panel.caption)} từ</Tag>
                          {panel.estimatedAudioDuration && (
                            <Tag color="orange">~{panel.estimatedAudioDuration}s audio</Tag>
                          )}
                        </Space>
                        <div style={{ padding: '8px', background: themeColors.bgWarning, borderRadius: 4, border: `1px solid ${themeColors.borderWarning}`, color: themeColors.textPrimary }}>
                          <Text strong>"{panel.localizedCaption || panel.caption}"</Text>
                        </div>
                        <div>
                          <Text strong>Visual Description:</Text>
                          <div style={{ marginTop: 4, fontSize: '13px' }}>
                            {panel.visualDescription}
                          </div>
                        </div>
                        {panel.characterIds && panel.characterIds.length > 0 && (
                          <div>
                            <Text type="secondary" style={{ marginRight: 8 }}>Nhân vật:</Text>
                            {panel.characterIds.map(id => (
                              <Tag key={id} color="blue">{getCharacterName(id)}</Tag>
                            ))}
                          </div>
                        )}
                      </Space>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          </Space>
        )}
      </Modal>

      {/* AI Review Result Modal */}
      <Modal
        title={`AI Review Scene - ${reviewScene?.title}`}
        open={isReviewModalVisible}
        onCancel={() => setIsReviewModalVisible(false)}
        width={700}
        footer={[
          <Button key="close" onClick={() => setIsReviewModalVisible(false)}>
            Đóng
          </Button>,
          reviewResult && (
            <Button 
              key="apply" 
              type="primary" 
              icon={<ThunderboltOutlined />} 
              onClick={handleApplyReviewSuggestion}
            >
              Sử dụng gợi ý để Re-extract
            </Button>
          )
        ]}
      >
        {!reviewResult ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin tip="AI đang phân tích và đối chiếu nội dung..." />
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {reviewResult.issues.length > 0 && (
              <div>
                <Title level={5}>Vấn đề chính (Issue Tags):</Title>
                <div style={{ marginBottom: 16 }}>
                  {reviewResult.issues.map((issue, idx) => (
                    <Tooltip key={idx} title={issue.description}>
                      <span style={{ cursor: 'help' }}>{renderIssueTag(issue.tag)}</span>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Title level={5}>Chi tiết nhận xét (Critiques):</Title>
              {reviewResult.critiques.length > 0 ? (
                <List
                  size="small"
                  dataSource={reviewResult.critiques}
                  renderItem={item => (
                    <List.Item>
                      <Text><Tag color="orange" style={{ borderRadius: '50%', width: 20, height: 20, textAlign: 'center', padding: 0 }}>!</Tag> {item}</Text>
                    </List.Item>
                  )}
                />
              ) : (
                <Alert message="Không có vấn đề gì đáng kể. Nội dung hiện tại đã khá tốt." type="success" showIcon />
              )}
            </div>

            <div>
              <Title level={5}>Gợi ý hiệu chỉnh (Suggested Comment):</Title>
              <div style={{ 
                padding: '12px', 
                background: themeColors.bgSuccess, 
                border: `1px solid ${themeColors.borderSuccess}`, 
                borderRadius: '8px',
                color: themeColors.textPrimary,
                position: 'relative'
              }}>
                <Text italic>"{reviewResult.suggestedComment}"</Text>
              </div>
              <Text type="secondary" style={{ fontSize: '12px', marginTop: 8, display: 'block' }}>
                Bấm nút dưới đây để copy gợi ý này vào luồng trích xuất lại (Re-extract) toàn bộ panel cho scene.
              </Text>
            </div>
          </Space>
        )}
      </Modal>

      {/* Media Strategy Picker Modal */}
      <Modal
        title="Chọn kiểu Media cho Panels"
        open={isMediaStrategyModalVisible}
        onCancel={() => setIsMediaStrategyModalVisible(false)}
        onOk={handleConfirmExtractPanels}
        okText="Extract Panels"
        cancelText="Hủy"
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text type="secondary">
            Chọn chiến lược tạo media cho các panel trong scene: <Text strong>{pendingExtractScene?.title}</Text>
          </Text>
          <Select
            value={mediaStrategy}
            onChange={(val) => setMediaStrategy(val)}
            style={{ width: '100%' }}
            size="large"
            options={[
              {
                value: 'AUTO_MIX',
                label: (
                  <Space>
                    <Tag color="purple">🤖 Auto Mix</Tag>
                    <Text>AI tự quyết định panel nào là Video, panel nào là Ảnh</Text>
                  </Space>
                ),
              },
              {
                value: 'ALL_IMAGE',
                label: (
                  <Space>
                    <Tag color="cyan">🖼️ All Image</Tag>
                    <Text>Toàn bộ panels là ảnh tĩnh (như cũ)</Text>
                  </Space>
                ),
              },
              {
                value: 'ALL_VIDEO',
                label: (
                  <Space>
                    <Tag color="volcano">🎬 All Video</Tag>
                    <Text>Toàn bộ panels sẽ là video</Text>
                  </Space>
                ),
              },
            ]}
          />
        </Space>
      </Modal>

      {selectedCharacterForImage && (
        <CharacterImageModal
          visible={isCharacterImageModalVisible}
          character={selectedCharacterForImage}
          storyId={storyId}
          onClose={() => {
            setIsCharacterImageModalVisible(false);
            setSelectedCharacterForImage(null);
            refreshCharacters();
          }}
        />
      )}

      {/* Edit Outfit Modal */}
      <Modal
        title="Edit Outfit Description"
        open={isEditOutfitModalVisible}
        onCancel={() => {
          setIsEditOutfitModalVisible(false);
          setEditingOutfitRef(null);
          outfitEditForm.resetFields();
        }}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              setIsEditOutfitModalVisible(false);
              setEditingOutfitRef(null);
              outfitEditForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="default"
            onClick={handleSaveOutfitDescription}
            loading={asyncLoading}
          >
            Save Description Only
          </Button>,
          <Button 
            key="regenerate" 
            type="primary"
            icon={<PictureOutlined />}
            onClick={handleRegenerateOutfitImage}
            loading={asyncLoading || generatingRefImageId === editingOutfitRef?.id}
          >
            Save & Regenerate Image
          </Button>,
        ]}
        width={700}
      >
        {editingOutfitRef && (
          <div>
            <Alert
              type="info"
              showIcon
              message="Edit Outfit Description"
              description={
                <div>
                  <p>Character: <strong>{getCharacterName(editingOutfitRef.characterId)}</strong></p>
                  <p>You can:</p>
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li><strong>Save Description Only:</strong> Update description without regenerating image</li>
                    <li><strong>Save & Regenerate Image:</strong> Update description and generate new outfit image based on it</li>
                  </ul>
                </div>
              }
              style={{ marginBottom: 16 }}
            />

            {editingOutfitRef.referenceUrl && (
              <div style={{ marginBottom: 16, textAlign: 'center' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Current Outfit Image:</Text>
                <Image 
                  src={editingOutfitRef.referenceUrl} 
                  alt="current outfit" 
                  style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }}
                />
              </div>
            )}

            <Form form={outfitEditForm} layout="vertical">
              <Form.Item
                label="Outfit Description"
                name="outfitDescription"
                rules={[{ required: true, message: 'Please enter outfit description' }]}
              >
                <TextArea 
                  rows={6}
                  placeholder="Describe the character's outfit, hair, accessories, etc. in this scene..."
                  showCount
                  maxLength={1000}
                />
              </Form.Item>
            </Form>

            <Alert
              type="warning"
              showIcon
              message="Note"
              description="Regenerating will create a new image. The old image will be replaced."
              style={{ marginTop: 16 }}
            />
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default ProcessTab;
